// Brand Studio frontend boundary. Backend routes intentionally remain under
// /api/master-order while the product is extracted at the UX level.
import { api } from "@/lib/api";

const p = (path) => `/master-order${path}`;

export const getThankYouSources = async () => (await api.get(p('/brand-applications/thank-you/sources'))).data;
export const preflightThankYou = async payload => (await api.post(p('/brand-applications/thank-you/preflight'),payload)).data;
const projects = '/brand-applications/projects';
export const listMaterialProjects = async (offset=0) => (await api.get(p(projects), {params:{offset}})).data;
export const getTagMaterialSources = async () => (await api.get(p(`${projects}/tag/sources`))).data;
export const saveTagMaterialProject = async (id, payload) => (await api.put(p(`${projects}/tag/${id}`), payload)).data;
export const getMaterialProject = async id => (await api.get(p(`${projects}/${id}`))).data;
export const saveMaterialProject = async (id, payload, blob) => {
  const body = new FormData();
  body.append('payload', JSON.stringify(payload));
  if (blob) body.append('png', blob, 'thank-you.png');
  return (await api.put(p(`${projects}/${id}`), body)).data;
};
export const getMaterialExport = async (id, media) => (await api.get(p(`${projects}/${id}/exports/${media}`), {responseType:'blob'})).data;
export const deleteMaterialProject = async (id, revision) => (await api.delete(p(`${projects}/${id}`), {params:{expected_revision:revision}})).data;
export const getPremiumOrderReady = async () => (await api.get(p(`${projects}/premium/ready`))).data;
export const generatePremiumOrderScene = async (id, payload, source, references=[]) => {
  const body=new FormData();body.append('payload',JSON.stringify(payload));body.append('source',source);
  references.forEach(file=>body.append('references',file));
  return (await api.post(p(`${projects}/${id}/premium-scene`),body,{timeout:120000})).data;
};

export const getBrandProfile = async () => (await api.get(p("/brand-profile"))).data;
export const saveBrandProfile = async (payload) =>
  (await api.post(p("/brand-profile"), payload)).data;
export const updateBrandProfile = async (payload) =>
  (await api.patch(p("/brand-profile"), payload)).data;
export const deleteBrandProfile = async () =>
  (await api.delete(p("/brand-profile"))).data;

export const getBrandProfileV2 = async () =>
  (await api.get(p("/brand-profile-v2"))).data;
export const createBrandProfileV2 = async (payload) =>
  (await api.post(p("/brand-profile-v2"), payload)).data;
export const updateBrandProfileV2 = async (payload) =>
  (await api.patch(p("/brand-profile-v2"), payload)).data;

export const getBrandCreationPriorities = async () =>
  (await api.get(p("/brand-creation-priorities"))).data;
export const saveBrandCreationPriorities = async (payload) =>
  (await api.put(p("/brand-creation-priorities"), payload)).data;

export const getLatestBrandStory = async () =>
  (await api.get(p("/brand-story/latest"))).data;
export const listConfirmedBrandStories = async () =>
  (await api.get(p("/brand-story/confirmed"))).data;
export const confirmBrandStory = async (payload) =>
  (await api.post(p("/brand-story/confirm"), payload)).data;

export const getLatestBrandPortrait = async () =>
  (await api.get(p("/brand-portrait/latest"))).data;
export const generateBrandPortrait = async () =>
  (await api.post(p("/brand-portrait/generate"), {}, { timeout: 200000 })).data;
export const reviseBrandPortrait = async (id, content) =>
  (await api.post(p(`/brand-portrait/${id}/revisions`), { content })).data;
export const confirmBrandPortrait = async (id) =>
  (await api.post(p(`/brand-portrait/${id}/confirm`))).data;

export const listExistingBrandSources = async () =>
  (await api.get(p("/existing-brand/sources"))).data;
export const createExistingBrandSource = async (payload) =>
  (await api.post(p("/existing-brand/sources"), payload)).data;
export const updateExistingBrandSource = async (id, payload) =>
  (await api.patch(p(`/existing-brand/sources/${id}`), payload)).data;
export const deleteExistingBrandSource = async (id) =>
  (await api.delete(p(`/existing-brand/sources/${id}`))).data;

export const listBrandReferences = async () =>
  (await api.get(p("/brand-references"))).data;
export const createBrandReference = async (payload) =>
  (await api.post(p("/brand-references"), payload)).data;
export const updateBrandReference = async (id, payload) =>
  (await api.patch(p(`/brand-references/${id}`), payload)).data;
export const deleteBrandReference = async (id) =>
  (await api.delete(p(`/brand-references/${id}`))).data;

export const getLogoIntake = async () =>
  (await api.get(p("/logo-intake"))).data;
export const saveLogoIntake = async (payload) =>
  (await api.put(p("/logo-intake"), payload)).data;

export const getLogoCore = async () => (await api.get(p("/logo-core"))).data;
export const saveLogoCore = async (payload) => (await api.post(p("/logo-core"), payload)).data;
export const approveLogoCore = async (expectedVersion) =>
  (await api.post(p("/logo-core/approve"), { expected_version: expectedVersion, explicit_confirmation: true })).data;

export const getExplicitVisualPreference = async () =>
  (await api.get(p("/explicit-visual-preference"))).data;
export const saveExplicitVisualPreference = async (payload) =>
  (await api.put(p("/explicit-visual-preference"), payload)).data;
export const getVisualEvidence = async () =>
  (await api.get(p("/visual-evidence"))).data;

export const runVisionAnalysisCapability = async (referenceId, retry = false) =>
  (
    await api.post(
      p(`/vision-analysis/capability-test/${referenceId}`),
      { retry },
    )
  ).data;

export const getVisionAnalysisPlan = async () =>
  (await api.get(p("/vision-analysis/plan"))).data;

export const runFullReferenceVisionOrchestration = async () =>
  (await api.post(p("/vision-analysis/orchestrate"))).data;

export const runVisualSynthesisCapability = async (retry = false) =>
  (
    await api.post(
      p("/visual-synthesis/capability-test"),
      { retry },
    )
  ).data;

export const runVisualUnderstandingAssembler = async () =>
  (await api.post(p("/visual-understanding-v2/assemble-test"))).data;

export const getStructuredVisualStyle = async () =>
  (await api.get(p("/visual-style-v1/current"))).data;

export const buildStructuredVisualStyle = async () =>
  (await api.post(p("/visual-style-v1/build"))).data;

export const getCurrentColorPaletteV1 = async () =>
  (await api.get(p("/color-palette-v1/current"))).data;

export const buildColorPaletteV1 = async () =>
  (await api.post(p("/color-palette-v1/build"))).data;

export const approveColorPaletteV1 = async () =>
  (await api.post(p("/color-palette-v1/approve"))).data;

export const refineColorPaletteV1 = async (payload) =>
  (await api.post(p("/color-palette-v1/refine"), payload)).data;

export const getVisualWorkflowPlanV1 = async () =>
  (await api.get(p("/visual-workflow-plan-v1"))).data;

export const getVisualWorkflowExecutionV1 = async () =>
  (await api.get(p("/visual-workflow-execution-v1"))).data;

export const getVisualProviderExecutionAdapterV1 = async () =>
  (await api.get(p("/visual-provider-execution-adapter-v1"))).data;

export const getVisualProviderOwnerPhotographyPreflight = async () =>
  (await api.get(p("/visual-provider-execution-v1/owner-run/preflight"))).data;

export const getLatestVisualProviderOwnerPhotography = async () =>
  (await api.get(p("/visual-provider-execution-v1/owner-run/latest"))).data;

export const runVisualProviderOwnerPhotography = async ({
  requestId,
  expectedPacketFingerprint,
}) =>
  (await api.post(p("/visual-provider-execution-v1/owner-run"), {
    request_id: requestId,
    workflow_type: "photography_direction",
    expected_packet_fingerprint: expectedPacketFingerprint,
  })).data;

export const getVisualProviderOwnerMaterialPreflight = async () =>
  (await api.get(p("/visual-provider-execution-v1/material/owner-run/preflight"))).data;

export const getLatestVisualProviderOwnerMaterial = async () =>
  (await api.get(p("/visual-provider-execution-v1/material/owner-run/latest"))).data;

export const saveVisualProviderOwnerMaterialReferenceOverride = async (
  selectedReferenceIds,
) =>
  (await api.put(p("/visual-provider-execution-v1/material/reference-override"), {
    selected_reference_ids: selectedReferenceIds,
  })).data;

export const resetVisualProviderOwnerMaterialReferenceOverride = async () =>
  (await api.delete(p("/visual-provider-execution-v1/material/reference-override"))).data;

export const runVisualProviderOwnerMaterial = async ({
  requestId,
  expectedPacketFingerprint,
}) =>
  (await api.post(p("/visual-provider-execution-v1/material/owner-run"), {
    request_id: requestId,
    workflow_type: "material_direction",
    expected_packet_fingerprint: expectedPacketFingerprint,
  })).data;

export const getVisualQualityGateContractV1 = async () =>
  (await api.get(p("/visual-quality-gate-v1/contract"))).data;

export const getVisualQualityEvaluatorPreflightV1 = async () =>
  (await api.get(p("/visual-quality-gate-v1/evaluator-preflight"))).data;




export const getVisualDirectionVnextFoundation = async () =>
  (await api.get(p("/visual-direction-v2/foundation"))).data;

export const getVisualDirectionGenerationContract = async (requestedOutputCount = null) =>
  (await api.get(p("/visual-direction-v2/generation-contract"), {
    params: requestedOutputCount ? { requested_output_count: requestedOutputCount } : {},
  })).data;

export const getVisualDirectionProviderPreflight = async () =>
  (await api.get(p("/visual-direction-v2/provider-preflight"))).data;

export const generateVisualDirectionOwnerQA = async (requestId) =>
  (await api.post(p("/visual-direction-v2/owner-generate"), { request_id: requestId })).data;

export const getLatestVisualDirectionOwnerQA = async () =>
  (await api.get(p("/visual-direction-v2/owner-generation/latest"))).data;

export const fetchAuthenticatedPrivateMediaBlob = async (fileUrl) => {
  const mediaPath = fileUrl.replace(/^\/api\/?/, "/");
  return (await api.get(mediaPath, { responseType: "blob" })).data;
};

export const getVisualSemanticPlan = async () =>
  (await api.get(p("/visual-understanding-v2/semantic-plan"))).data;

export const runVisualSemanticClustering = async () =>
  (await api.post(p("/visual-understanding-v2/semantic-cluster-test"))).data;

export const getTypographyAnchor = async () =>
  (await api.get(p("/typography-anchor"))).data;
export const saveTypographyAnchor = async (payload) =>
  (await api.put(p("/typography-anchor"), payload)).data;
export const deleteTypographyAnchor = async () =>
  (await api.delete(p("/typography-anchor"))).data;

export const getTypographyPreferences = async () =>
  (await api.get(p("/typography-preferences"))).data;
export const saveTypographySystem = async (payload) =>
  (await api.post(p("/typography-preferences/system"), payload)).data;
export const approveTypographySystem = async (expectedVersion) =>
  (await api.post(p("/typography-preferences/system/approve"), { expected_version: expectedVersion })).data;
export const createTypographyPreference = async (payload) =>
  (await api.post(p("/typography-preferences"), payload)).data;
export const updateTypographyPreference = async (id, payload) =>
  (await api.patch(p(`/typography-preferences/${id}`), payload)).data;
export const deleteTypographyPreference = async (id) =>
  (await api.delete(p(`/typography-preferences/${id}`))).data;

export const listFontCatalog = async (params = {}) =>
  (await api.get(p("/font-catalog"), { params })).data;

export const uploadPrivateFontAsset = async (file, rightsConfirmed) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("rights_confirmed", rightsConfirmed ? "true" : "false");
  return (await api.post(p("/font-assets"), fd, {
    headers: { "Content-Type": "multipart/form-data" },
  })).data;
};
export const deletePrivateFontAsset = async (id) =>
  (await api.delete(p(`/font-assets/${id}`))).data;
export const fetchPrivateFontBlob = async (fileUrl) => {
  const mediaPath = fileUrl.replace(/^\/api\/?/, "/");
  return (await api.get(mediaPath, { responseType: "blob" })).data;
};

// Reuse the existing authenticated private-media upload contract.
export const uploadUserMediaFile = async (file, { title } = {}) => {
  const fd = new FormData();
  fd.append("file", file);
  if (title) fd.append("title", title);
  const { data } = await api.post("/account/media/upload-file", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

export const getVisualDirectionWorkspace = async () =>
  (await api.get(p("/visual-direction"))).data;
export const selectVisualDirection = async (directionId) =>
  (await api.post(p(`/visual-direction/${directionId}/select`))).data;
export const saveVisualDirectionFeedback = async (directionId, feedback, preserveFeedback = "") =>
  (await api.post(p(`/visual-direction/${directionId}/feedback`), {
    feedback, preserve_feedback: preserveFeedback || null,
  })).data;
export const refineVisualDirection = async (directionId) =>
  (await api.post(p(`/visual-direction/${directionId}/refine`))).data;
export const getVisualDirectionRefinementStatus = async () =>
  (await api.get(p("/visual-direction/refinement-status"))).data;
export const generateVisualDirections = async ({ retry = false } = {}) =>
  (await api.post(p("/visual-direction/generate"), { retry })).data;
export const runVisualSearchSmoke = async ({ retry = false } = {}) =>
  (await api.post(p("/visual-search/smoke-test"), { retry }, { timeout: 120000 })).data;
export const checkVisualDirectionBoardProviderAccess = async () =>
  (await api.post(p("/visual-direction/board/provider-access-check"))).data;
export const generateVisualDirectionBoard = async (directionId) =>
  (await api.post(p(`/visual-direction/${directionId}/board/generate`))).data;
export const refineVisualDirectionBoard = async (directionId) =>
  (await api.post(p(`/visual-direction/${directionId}/board/refine`))).data;
export const makeVisualDirectionBoardCurrent = async (directionId, boardId) =>
  (await api.post(p(`/visual-direction/${directionId}/boards/${boardId}/make-current`))).data;
export const getVisualDirectionVisualRefinementStatus = async () =>
  (await api.get(p("/visual-direction/visual-refinement-status"))).data;
export const approveVisualDirection = async (directionId) =>
  (await api.post(p(`/visual-direction/${directionId}/approve`))).data;
export const getVisualDNAWorkspace = async () =>
  (await api.get(p("/visual-dna"))).data;
export const generateLogoFamily = async ({ retry = false } = {}) =>
  (await api.post(p("/visual-dna/logo-family/generate"), { retry })).data;
export const saveLogoFamilyFeedback = async (feedback, preserveFeedback) =>
  (await api.post(p("/visual-dna/logo-family/feedback"), {
    feedback, preserve_feedback: preserveFeedback,
  })).data;
export const makeLogoFamilyVersionCurrent = async (versionId) =>
  (await api.post(p(`/visual-dna/logo-family/versions/${versionId}/make-current`))).data;
export const approveLogoFamily = async () =>
  (await api.post(p("/visual-dna/logo-family/approve"))).data;
export const generateColorSystem = async ({ retry = false } = {}) =>
  (await api.post(p("/visual-dna/color-system/generate"), { retry })).data;
export const saveColorSystemFeedback = async (feedback, preserveFeedback) =>
  (await api.post(p("/visual-dna/color-system/feedback"), {
    feedback, preserve_feedback: preserveFeedback,
  })).data;
export const makeColorSystemVersionCurrent = async (versionId) =>
  (await api.post(p(`/visual-dna/color-system/versions/${versionId}/make-current`))).data;
export const approveColorSystem = async () =>
  (await api.post(p("/visual-dna/color-system/approve"))).data;
export const generateTypography = async ({ retry = false } = {}) =>
  (await api.post(p("/visual-dna/typography/generate"), { retry })).data;
export const saveTypographyFeedback = async (feedback, preserveFeedback) =>
  (await api.post(p("/visual-dna/typography/feedback"), {
    feedback, preserve_feedback: preserveFeedback,
  })).data;
export const makeTypographyVersionCurrent = async (versionId) =>
  (await api.post(p(`/visual-dna/typography/versions/${versionId}/make-current`))).data;
export const approveTypography = async () =>
  (await api.post(p("/visual-dna/typography/approve"))).data;

export const generateGraphicSystem = async ({ retry = false, operationId = null } = {}) =>
  (await api.post(p("/visual-dna/graphic-system/generate"), {
    retry, operation_id: operationId,
  })).data;
export const getGraphicSystemForensicStatus = async () =>
  (await api.get(p("/visual-dna/graphic-system/forensic-status"))).data;
export const saveGraphicSystemFeedback = async (feedback, preserveFeedback) =>
  (await api.post(p("/visual-dna/graphic-system/feedback"), {
    feedback, preserve_feedback: preserveFeedback,
  })).data;
export const makeGraphicSystemVersionCurrent = async (versionId) =>
  (await api.post(p(`/visual-dna/graphic-system/versions/${versionId}/make-current`))).data;
export const approveGraphicSystem = async () =>
  (await api.post(p("/visual-dna/graphic-system/approve"))).data;

export const generatePatternSystem = async ({ retry = false, operationId = null } = {}) =>
  (await api.post(p("/visual-dna/patterns/generate"), {
    retry, operation_id: operationId,
  })).data;
export const savePatternSystemFeedback = async (feedback, preserveFeedback) =>
  (await api.post(p("/visual-dna/patterns/feedback"), {
    feedback, preserve_feedback: preserveFeedback,
  })).data;
export const makePatternSystemVersionCurrent = async (versionId) =>
  (await api.post(p("/visual-dna/patterns/versions/" + versionId + "/make-current"))).data;
export const approvePatternSystem = async () =>
  (await api.post(p("/visual-dna/patterns/approve"))).data;

